import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { AuthSsoButtons } from './AuthSsoButtons';
import { AuthEmailHeading, AuthMode } from './AuthEmailHeading';
import { AuthFieldError } from './AuthFieldError';
import { AuthLegal } from './AuthLegal';
import { authErrorKind } from './authErrorKind';
import { AuthRateLimitText } from './AuthRateLimitText';
import { authV2Messages } from './authV2Messages';
import { useAuthTrialFacts } from './useAuthTrialFacts';

export interface AuthInviteInfo {
  workspaceName: string | null;
  inviterName: string | null;
}

interface AuthEmailStepProps {
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  /** "I already have a code": the code step for this address, with no new email (QA brk-6). */
  onHaveCode?: () => void;
  isLoading: boolean;
  error: Error | null;
  /** `signin` shows the copy of design g10. Defaults to `signup` (g1). */
  mode?: AuthMode;
  /** Set when sign-in was opened by a team invite link (design g7). */
  invite?: AuthInviteInfo | null;
  /** The hidden Turnstile check (`TurnstileSlot`). It shows only when Cloudflare asks. */
  securityCheck?: React.ReactNode;
  /** The check waits for a tick (`useTurnstile().needsInteraction`): the send stays off. */
  securityCheckPending?: boolean;
  /** Under the email form: the password sign-in of the OAuth flow (`AuthPasswordSignIn`), else nothing. */
  afterForm?: React.ReactNode;
  /** Shown right under the Apple and Google buttons: one more way to sign in. */
  afterSso?: React.ReactNode;
}

/**
 * The email rule of the server (zod `email()` in music24-api `auth.requestCode`), so the page
 * refuses what the server refuses: '<script>@x.com' passed the old loose rule and gave a 400
 * with "contact support" (QA 2026-10-28, brk-r2-1).
 */
export const EMAIL_RE =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;
const EMAIL_ERROR_ID = 'sml-auth-email-err';
/** The longest email address (music24-api `LOGIN_EMAIL_MAX_LENGTH`, QA brk-9). */
const EMAIL_MAX_LENGTH = 254;

/**
 * The email step. One field does both jobs: a new address makes the free account, a known one
 * signs in. design: onboarding g1, g7, g8, g10; the field errors are in g6.
 */
const AuthEmailStepComponent: React.FC<AuthEmailStepProps> = ({
  email,
  setEmail,
  onSubmit,
  onHaveCode,
  isLoading,
  error,
  mode = 'signup',
  invite = null,
  securityCheck = null,
  securityCheckPending = false,
  afterForm = null,
  afterSso = null,
}) => {
  const intl = useIntl();
  const trial = useAuthTrialFacts();
  const [touched, setTouched] = useState(false);
  // Continue stays on: a press with a bad address says why, in the field (QA brk-20).
  const [submitTried, setSubmitTried] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = email.trim();
  const valid = EMAIL_RE.test(trimmed);
  const errorKind = authErrorKind(error);
  const isDisposableEmail = errorKind === 'disposable';
  const showMissing = !isDisposableEmail && submitTried && trimmed === '';
  const showInvalid =
    errorKind === 'invalidEmail' || (!isDisposableEmail && (touched || submitTried) && trimmed !== '' && !valid);
  let fieldError: string | null = null;
  if (isDisposableEmail) {
    fieldError = intl.formatMessage(authV2Messages.emailDisposable);
  } else if (showMissing) {
    fieldError = intl.formatMessage(authV2Messages.emailMissing);
  } else if (showInvalid) {
    fieldError = intl.formatMessage(authV2Messages.emailInvalid);
  }

  const submit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!valid) {
        setSubmitTried(true);
        inputRef.current?.focus();
        return;
      }
      onSubmit(event);
    },
    [onSubmit, valid]
  );
  const isInvite = invite !== null;

  const smallLine = (() => {
    if (isInvite) {
      return intl.formatMessage(authV2Messages.inviteNewAccount);
    }
    if (mode === 'signin') {
      return intl.formatMessage(authV2Messages.signinNewEmail, { days: trial.days, plan: trial.plan });
    }
    return intl.formatMessage(authV2Messages.oneField);
  })();

  return (
    <div className="au-step">
      <AuthEmailHeading
        mode={mode}
        isInvite={isInvite}
        workspaceName={invite?.workspaceName ?? null}
        inviterName={invite?.inviterName ?? null}
      />

      <form className="au-form" onSubmit={submit} noValidate>
        {errorKind === 'rateLimit' ? (
          <AuthFieldError id="sml-auth-rate-limit">
            <AuthRateLimitText error={error} />
          </AuthFieldError>
        ) : null}
        {errorKind === 'turnstile' ? (
          <AuthFieldError>{intl.formatMessage(authV2Messages.securityCheckFailed)}</AuthFieldError>
        ) : null}
        {error !== null && (errorKind === 'other' || errorKind === 'badCode') ? <ErrorAlert error={error} /> : null}

        <div className={fieldError !== null ? 'field err' : 'field'}>
          <label className="au-lbl" htmlFor="sml-auth-email">
            {intl.formatMessage(authV2Messages.emailLabel)}
          </label>
          <div className="au-in">
            <input
              ref={inputRef}
              id="sml-auth-email"
              type="email"
              autoComplete="email"
              maxLength={EMAIL_MAX_LENGTH}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isLoading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={intl.formatMessage(authV2Messages.emailPlaceholder)}
              aria-invalid={fieldError !== null}
              aria-describedby={fieldError !== null ? EMAIL_ERROR_ID : undefined}
            />
          </div>
          {fieldError !== null ? (
            <div className="field-err" id={EMAIL_ERROR_ID}>
              <FontAwesomeIcon icon={faCircleExclamation} />
              <span>{fieldError}</span>
            </div>
          ) : null}
        </div>
        {securityCheck}
        <SMLButton
          color="secondary"
          onClick={submit}
          disabled={isLoading || securityCheckPending}
          isLoading={isLoading}
          stretch
          omitCap
          size="lg"
          submit
        >
          {mode === 'signin' && !isInvite
            ? intl.formatMessage(authV2Messages.signinCta)
            : intl.formatMessage(authV2Messages.continue)}
          <FontAwesomeIcon icon={faChevronRight} />
        </SMLButton>

        {onHaveCode !== undefined && valid ? (
          <button type="button" className="au-link muted au-have-code" onClick={onHaveCode} disabled={isLoading}>
            {intl.formatMessage(authV2Messages.haveCode)}
          </button>
        ) : null}

        <AuthSsoButtons />
        {afterSso}

        <p className="au-small">{smallLine}</p>
        <AuthLegal />
      </form>
      {afterForm}
    </div>
  );
};

export const AuthEmailStep = TypedMemo(AuthEmailStepComponent);
